import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  MessageSquare,
  Users,
  Building2,
  Plus,
  Search,
  Send,
  ChevronDown,
  UserPlus,
  Loader2,
  X,
  Info,
  Smile,
  CheckCheck,
  Copy,
  Hash,
  Sparkles,
  ArrowLeft,
  ChevronRight,
  Shield,
  MoreVertical,
  Reply,
  Trash2,
} from 'lucide-react';
import { MultiSelect } from '@/shared/components/ui/MultiSelect';
import { Spinner } from '@/shared/components/ui/Spinner';
import { toast } from 'react-hot-toast';
import { getApiErrorMessage } from '@/shared/api/axios';
import {
  useChatConversations,
  useChatMessages,
  useChatMutations,
  messagesFromPages,
  ChatConversation,
  ChatMessage,
} from '../api';
import { useAllUsers, UserOption } from '../api/users';
import { useChatRealtime } from '../useChatRealtime';

function formatMessageTime(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

function formatDateSeparator(dateString: string) {
  const date = new Date(dateString);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  if (isToday) return 'Today';
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

function formatRelativeTime(dateString: string) {
  const date = new Date(dateString);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  if (isToday) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  }
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function getInitials(name: string) {
  return name.split(' ').filter(Boolean).map((n) => n[0]).join('').slice(0, 2).toUpperCase();
}

function getAvatarBg(id: string) {
  const colors = [
    'bg-indigo-500 text-white',
    'bg-emerald-500 text-white',
    'bg-amber-500 text-white',
    'bg-rose-500 text-white',
    'bg-sky-500 text-white',
    'bg-purple-500 text-white',
    'bg-teal-500 text-white',
    'bg-blue-600 text-white',
  ];
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return colors[hash % colors.length];
}

const COMMON_EMOJIS = ['👍', '❤️', '🔥', '🎉', '🚀', '👀', '👏', '✅', '💡'];

type FilterTab = 'all' | 'dm' | 'group' | 'department';

interface ConversationListProps {
  conversations: ChatConversation[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onOpenCreateDm: () => void;
  onOpenCreateGroup: () => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  activeTab: FilterTab;
  onTabChange: (tab: FilterTab) => void;
}

function ConversationList({
  conversations,
  activeId,
  onSelect,
  onOpenCreateDm,
  onOpenCreateGroup,
  searchQuery,
  onSearchChange,
  activeTab,
  onTabChange,
}: ConversationListProps) {
  const query = searchQuery.trim().toLowerCase();

  const tabFiltered = useMemo(() => {
    return conversations.filter((c) => {
      if (activeTab === 'all') return true;
      if (activeTab === 'dm') return c.type === 'dm';
      if (activeTab === 'group') return c.type === 'group';
      if (activeTab === 'department') return c.type === 'department' || c.type === 'company';
      return true;
    });
  }, [conversations, activeTab]);

  const searchFiltered = useMemo(() => {
    if (!query) return tabFiltered;
    return tabFiltered.filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        (c.lastMessagePreview && c.lastMessagePreview.toLowerCase().includes(query)) ||
        (c.department && c.department.toLowerCase().includes(query))
    );
  }, [tabFiltered, query]);

  const counts = useMemo(() => {
    return {
      all: conversations.length,
      dm: conversations.filter((c) => c.type === 'dm').length,
      group: conversations.filter((c) => c.type === 'group').length,
      department: conversations.filter((c) => c.type === 'department' || c.type === 'company').length,
    };
  }, [conversations]);

  return (
    <div className="flex flex-col h-full bg-card/60 backdrop-blur-xs select-none">
      {/* Sidebar Header */}
      <div className="p-4 border-b border-border/70 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-primary to-indigo-600 flex items-center justify-center text-primary-foreground shadow-xs">
              <MessageSquare className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-bold text-base text-foreground tracking-tight flex items-center gap-2">
                <span>ZYNX Chats</span>
                <span className="flex h-2 w-2 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
              </h1>
              <p className="text-[11px] text-muted-foreground font-medium">Team messaging & channels</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={onOpenCreateDm}
              title="New Direct Message"
              className="p-2 rounded-lg hover:bg-accent/80 text-muted-foreground hover:text-foreground transition-all cursor-pointer"
            >
              <Plus className="w-4 h-4" />
            </button>
            <button
              onClick={onOpenCreateGroup}
              title="Create New Group"
              className="p-2 rounded-lg hover:bg-accent/80 text-muted-foreground hover:text-foreground transition-all cursor-pointer"
            >
              <UserPlus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/70 pointer-events-none" />
          <input
            type="text"
            placeholder="Search chats, people, groups..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-8 py-2 bg-muted/50 border border-border/70 rounded-xl text-xs text-foreground placeholder:text-muted-foreground/70 focus:ring-2 focus:ring-primary/20 focus:border-primary/60 outline-none transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-muted text-muted-foreground"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Category Tabs */}
        <div className="flex items-center p-1 bg-muted/40 rounded-xl border border-border/50 text-xs font-medium text-muted-foreground">
          <button
            onClick={() => onTabChange('all')}
            className={`flex-1 py-1 px-2 rounded-lg text-center transition-all flex items-center justify-center gap-1.5 ${
              activeTab === 'all'
                ? 'bg-card text-foreground font-semibold shadow-2xs'
                : 'hover:text-foreground hover:bg-card/40'
            }`}
          >
            <span>All</span>
            <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-muted text-muted-foreground font-mono">
              {counts.all}
            </span>
          </button>
          <button
            onClick={() => onTabChange('dm')}
            className={`flex-1 py-1 px-2 rounded-lg text-center transition-all flex items-center justify-center gap-1.5 ${
              activeTab === 'dm'
                ? 'bg-card text-foreground font-semibold shadow-2xs'
                : 'hover:text-foreground hover:bg-card/40'
            }`}
          >
            <span>DMs</span>
            <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-muted text-muted-foreground font-mono">
              {counts.dm}
            </span>
          </button>
          <button
            onClick={() => onTabChange('group')}
            className={`flex-1 py-1 px-2 rounded-lg text-center transition-all flex items-center justify-center gap-1.5 ${
              activeTab === 'group'
                ? 'bg-card text-foreground font-semibold shadow-2xs'
                : 'hover:text-foreground hover:bg-card/40'
            }`}
          >
            <span>Groups</span>
            <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-muted text-muted-foreground font-mono">
              {counts.group}
            </span>
          </button>
          <button
            onClick={() => onTabChange('department')}
            className={`flex-1 py-1 px-2 rounded-lg text-center transition-all flex items-center justify-center gap-1.5 ${
              activeTab === 'department'
                ? 'bg-card text-foreground font-semibold shadow-2xs'
                : 'hover:text-foreground hover:bg-card/40'
            }`}
          >
            <span>Channels</span>
          </button>
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {searchFiltered.map((conv) => {
          const isActive = activeId === conv.id;
          return (
            <button
              key={conv.id}
              onClick={() => onSelect(conv.id)}
              className={`w-full p-2.5 rounded-xl text-left transition-all flex items-center gap-3 relative group cursor-pointer ${
                isActive
                  ? 'bg-primary/10 text-primary border border-primary/25 shadow-2xs font-medium'
                  : 'hover:bg-accent/60 text-foreground border border-transparent'
              }`}
            >
              {/* Avatar Icon */}
              <div className="relative flex-shrink-0">
                <div
                  className={`w-11 h-11 rounded-xl flex items-center justify-center font-bold text-sm shadow-2xs transition-transform group-hover:scale-105 ${
                    conv.type === 'company'
                      ? 'bg-gradient-to-tr from-sky-500 to-blue-600 text-white'
                      : conv.type === 'department'
                      ? 'bg-gradient-to-tr from-emerald-500 to-teal-600 text-white'
                      : conv.type === 'group'
                      ? 'bg-gradient-to-tr from-amber-500 to-orange-600 text-white'
                      : getAvatarBg(conv.id)
                  }`}
                >
                  {conv.type === 'company' && <Building2 className="w-5 h-5" />}
                  {conv.type === 'department' && <Hash className="w-5 h-5" />}
                  {conv.type === 'group' && <Users className="w-5 h-5" />}
                  {conv.type === 'dm' && getInitials(conv.name)}
                </div>

                {conv.unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center shadow-xs">
                    {conv.unreadCount > 9 ? '9+' : conv.unreadCount}
                  </span>
                )}
              </div>

              {/* Text Meta */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-1 mb-0.5">
                  <span className={`text-xs truncate ${isActive ? 'font-bold text-foreground' : 'font-semibold text-foreground'}`}>
                    {conv.name || 'Direct Message'}
                  </span>
                  {conv.lastMessageAt && (
                    <span className="text-[10px] text-muted-foreground/80 flex-shrink-0 font-medium">
                      {formatRelativeTime(conv.lastMessageAt)}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] text-muted-foreground truncate line-clamp-1">
                    {conv.lastMessagePreview ? (
                      <>
                        {conv.lastMessageByName && conv.type !== 'dm' && (
                          <span className="font-semibold text-foreground/80">{conv.lastMessageByName}: </span>
                        )}
                        <span>{conv.lastMessagePreview}</span>
                      </>
                    ) : (
                      <span className="italic text-muted-foreground/60">No messages yet</span>
                    )}
                  </p>
                  {conv.department && conv.type === 'department' && (
                    <span className="text-[9px] uppercase px-1.5 py-0.5 bg-muted rounded-md text-muted-foreground font-semibold flex-shrink-0">
                      {conv.department}
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}

        {searchFiltered.length === 0 && (
          <div className="text-center py-12 px-4 space-y-3">
            <div className="w-12 h-12 rounded-2xl bg-muted/60 text-muted-foreground flex items-center justify-center mx-auto">
              <MessageSquare className="w-6 h-6 opacity-60" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">
                {query ? 'No matching conversations' : 'No chats in this category'}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {query ? 'Try searching another name' : 'Start a new message or group to get talking'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface ChatAreaHeaderProps {
  conversation: ChatConversation;
  onBack: () => void;
  onToggleInfo: () => void;
  isInfoOpen: boolean;
}

function ChatAreaHeader({ conversation, onBack, onToggleInfo, isInfoOpen }: ChatAreaHeaderProps) {
  return (
    <div className="px-5 py-3 border-b border-border bg-card/90 backdrop-blur-md flex items-center justify-between gap-3 sticky top-0 z-10 shadow-2xs">
      <div className="flex items-center gap-3 min-w-0">
        <button
          onClick={onBack}
          className="lg:hidden p-1.5 -ml-1 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
          aria-label="Back to conversations list"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>

        <div className="relative flex-shrink-0">
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm shadow-2xs ${
              conversation.type === 'company'
                ? 'bg-gradient-to-tr from-sky-500 to-blue-600 text-white'
                : conversation.type === 'department'
                ? 'bg-gradient-to-tr from-emerald-500 to-teal-600 text-white'
                : conversation.type === 'group'
                ? 'bg-gradient-to-tr from-amber-500 to-orange-600 text-white'
                : getAvatarBg(conversation.id)
            }`}
          >
            {conversation.type === 'company' && <Building2 className="w-5 h-5" />}
            {conversation.type === 'department' && <Hash className="w-5 h-5" />}
            {conversation.type === 'group' && <Users className="w-5 h-5" />}
            {conversation.type === 'dm' && getInitials(conversation.name)}
          </div>
          <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-card bg-emerald-500" />
        </div>

        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-bold text-foreground truncate tracking-tight">
              {conversation.name || 'Direct Message'}
            </h2>
            <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase bg-muted text-muted-foreground border border-border/40">
              {conversation.type}
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground truncate flex items-center gap-1.5">
            {conversation.department ? (
              <span>Department: {conversation.department}</span>
            ) : conversation.memberCount ? (
              <span>{conversation.memberCount} members</span>
            ) : (
              <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Active in workspace
              </span>
            )}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-1.5">
        <button
          onClick={onToggleInfo}
          className={`p-2 rounded-xl border transition-all cursor-pointer ${
            isInfoOpen
              ? 'bg-primary text-primary-foreground border-primary'
              : 'hover:bg-muted text-muted-foreground hover:text-foreground border-border/60'
          }`}
          title="Conversation Details"
        >
          <Info className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function MessageActions({
  message,
  onReply,
  onDelete,
}: {
  message: ChatMessage;
  onReply: (message: ChatMessage) => void;
  onDelete: (message: ChatMessage) => void;
}) {
  return (
    <div className="opacity-0 group-hover/bubble:opacity-100 transition-opacity flex items-center gap-0.5 bg-card border border-border/80 rounded-lg p-0.5 shadow-xs">
      <button
        onClick={() => onReply(message)}
        className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground cursor-pointer"
        title="Reply"
      >
        <Reply className="w-3 h-3" />
      </button>
      {!message.isDeleted && (
        <button
          onClick={() => {
            navigator.clipboard.writeText(message.content);
            toast.success('Message copied to clipboard');
          }}
          className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground cursor-pointer"
          title="Copy message"
        >
          <Copy className="w-3 h-3" />
        </button>
      )}
      {/* Only the sender may delete, and the text is hidden rather than erased. */}
      {message.mine && !message.isDeleted && (
        <button
          onClick={() => onDelete(message)}
          className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive cursor-pointer"
          title="Delete message"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

interface MessageListProps {
  messages: ChatMessage[];
  onLoadMore: () => void;
  hasMore: boolean;
  loading: boolean;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  conversationName: string;
  onReply: (message: ChatMessage) => void;
  onDelete: (message: ChatMessage) => void;
}

function MessageList({
  messages,
  onLoadMore,
  hasMore,
  loading,
  messagesEndRef,
  conversationName,
  onReply,
  onDelete,
}: MessageListProps) {
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          onLoadMore();
        }
      },
      { threshold: 0.1 }
    );
    if (loadMoreRef.current) observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, onLoadMore]);

  return (
    <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4 bg-gradient-to-b from-muted/15 via-background to-muted/25 dark:from-background dark:to-muted/5">
      {hasMore && (
        <div ref={loadMoreRef} className="text-center py-2">
          {loading ? (
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-card border border-border text-xs text-muted-foreground shadow-2xs">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
              <span>Loading earlier messages...</span>
            </div>
          ) : (
            <button
              onClick={onLoadMore}
              className="text-xs font-semibold px-3 py-1 rounded-full bg-card hover:bg-muted border border-border text-muted-foreground hover:text-foreground transition-all cursor-pointer shadow-2xs"
            >
              ↑ Load earlier messages
            </button>
          )}
        </div>
      )}

      {messages.length === 0 && (
        <div className="py-20 text-center space-y-3">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto shadow-2xs">
            <Sparkles className="w-7 h-7" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-foreground">Welcome to {conversationName}</h3>
            <p className="text-xs text-muted-foreground max-w-sm mx-auto mt-1">
              This is the beginning of your conversation. Send a message below to start the thread.
            </p>
          </div>
        </div>
      )}

      {messages.map((msg, idx) => {
        const isMine = msg.mine;
        const prevMsg = messages[idx - 1];
        const nextMsg = messages[idx + 1];

        // Date separator logic
        const showDateSeparator =
          !prevMsg ||
          new Date(prevMsg.createdAt).toDateString() !== new Date(msg.createdAt).toDateString();

        // Avatar & sender name grouping
        const isFirstInSeries = !prevMsg || prevMsg.senderId !== msg.senderId || showDateSeparator;
        const isLastInSeries = !nextMsg || nextMsg.senderId !== msg.senderId;

        return (
          <React.Fragment key={msg.id}>
            {showDateSeparator && (
              <div className="flex items-center justify-center my-4">
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-card/80 backdrop-blur-xs border border-border/70 px-3 py-1 rounded-full shadow-2xs">
                  {formatDateSeparator(msg.createdAt)}
                </span>
              </div>
            )}

            <div
              className={`flex items-end gap-2 group ${
                isMine ? 'justify-end' : 'justify-start'
              } ${isFirstInSeries ? 'mt-3' : 'mt-1'}`}
            >
              {/* Other User Avatar */}
              {!isMine && (
                <div className="w-7 h-7 flex-shrink-0">
                  {isLastInSeries ? (
                    <div
                      className={`w-7 h-7 rounded-lg flex items-center justify-center font-bold text-[11px] shadow-2xs ${getAvatarBg(
                        msg.senderId
                      )}`}
                      title={msg.senderName}
                    >
                      {getInitials(msg.senderName)}
                    </div>
                  ) : (
                    <div className="w-7 h-7" />
                  )}
                </div>
              )}

              {/* Message Bubble Column */}
              <div className={`flex flex-col max-w-[75%] sm:max-w-[65%] ${isMine ? 'items-end' : 'items-start'}`}>
                {/* Sender Name above received bubble */}
                {!isMine && isFirstInSeries && (
                  <span className="text-[11px] font-bold text-foreground/80 mb-1 px-1">
                    {msg.senderName}
                  </span>
                )}

                <div className="relative group/bubble flex items-center gap-1.5">
                  {isMine && (
                    <MessageActions message={msg} onReply={onReply} onDelete={onDelete} />
                  )}

                  {/* Bubble Container */}
                  <div
                    className={`px-4 py-2.5 text-xs rounded-2xl shadow-2xs relative leading-relaxed transition-all ${
                      isMine
                        ? 'bg-gradient-to-br from-primary to-primary/95 text-primary-foreground rounded-br-xs font-normal'
                        : 'bg-card border border-border/80 text-foreground rounded-bl-xs'
                    }`}
                  >
                    {msg.replyTo && (
                      <div
                        className={`mb-1.5 rounded-lg border-l-2 px-2.5 py-1.5 ${
                          isMine
                            ? 'border-primary-foreground/60 bg-primary-foreground/10'
                            : 'border-primary/60 bg-primary/5'
                        }`}
                      >
                        <p className="text-[10px] font-bold opacity-80">{msg.replyTo.senderName}</p>
                        <p className="text-[10px] opacity-70 line-clamp-2 break-words">
                          {msg.replyTo.content || 'This message was deleted'}
                        </p>
                      </div>
                    )}
                    {msg.isDeleted ? (
                      <p className="italic opacity-75">This message was deleted</p>
                    ) : (
                      <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                    )}
                    <div
                      className={`flex items-center justify-end gap-1 mt-1 text-[9px] font-medium ${
                        isMine ? 'text-primary-foreground/75' : 'text-muted-foreground/70'
                      }`}
                    >
                      <span>{formatMessageTime(msg.createdAt)}</span>
                      {isMine && <CheckCheck className="w-3 h-3 text-primary-foreground/90" />}
                    </div>
                  </div>

                  {!isMine && <MessageActions message={msg} onReply={onReply} onDelete={onDelete} />}
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      })}
      <div ref={messagesEndRef} />
    </div>
  );
}

interface MessageInputProps {
  conversation: ChatConversation | null;
  onSend: (content: string) => void;
  disabled?: boolean;
  replyTo: ChatMessage | null;
  onCancelReply: () => void;
}

function MessageInput({ conversation, onSend, disabled, replyTo, onCancelReply }: MessageInputProps) {
  const [content, setContent] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = () => {
    if (content.trim() && conversation) {
      onSend(content.trim());
      setContent('');
      setShowEmojiPicker(false);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const insertEmoji = (emoji: string) => {
    setContent((prev) => prev + emoji);
    textareaRef.current?.focus();
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 140)}px`;
    }
  }, [content]);

  useEffect(() => {
    if (replyTo) textareaRef.current?.focus();
  }, [replyTo]);

  return (
    <div className="p-3 bg-card border-t border-border relative">
      {/* Quick Emoji Bar Popup */}
      {showEmojiPicker && (
        <div className="absolute bottom-full left-4 mb-2 p-2 bg-card/95 backdrop-blur-md border border-border rounded-xl shadow-lg flex items-center gap-1.5 z-20 animate-in fade-in zoom-in-95 duration-100">
          {COMMON_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              onClick={() => insertEmoji(emoji)}
              className="w-8 h-8 rounded-lg hover:bg-muted text-base flex items-center justify-center transition-transform hover:scale-125 cursor-pointer"
            >
              {emoji}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2 max-w-5xl mx-auto">
        <div className="flex-1 relative flex flex-col bg-muted/40 border border-border/80 focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/15 rounded-2xl transition-all shadow-2xs">
          {replyTo && (
            <div className="flex items-start gap-2 px-3 pt-2">
              <div className="flex-1 min-w-0 rounded-lg border-l-2 border-primary bg-primary/5 px-2.5 py-1.5">
                <p className="text-[10px] font-bold text-primary">Replying to {replyTo.senderName}</p>
                <p className="text-[10px] text-muted-foreground truncate">
                  {replyTo.isDeleted ? 'This message was deleted' : replyTo.content}
                </p>
              </div>
              <button
                onClick={onCancelReply}
                className="p-1 mt-0.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground cursor-pointer"
                title="Cancel reply"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          <div className="flex items-end">
            <button
              type="button"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className="p-3 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
              title="Add Emoji"
            >
              <Smile className="w-5 h-5" />
            </button>

            <textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={`Message ${conversation ? conversation.name : 'in chat'}...`}
              disabled={disabled || !conversation}
              rows={1}
              className="flex-1 py-3 pr-3 bg-transparent border-0 text-xs text-foreground placeholder:text-muted-foreground/70 resize-none outline-none disabled:opacity-50 min-h-[44px] max-h-[140px]"
            />
          </div>
        </div>

        <button
          onClick={handleSend}
          disabled={!content.trim() || disabled || !conversation}
          className="p-3 bg-primary text-primary-foreground rounded-2xl hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-xs cursor-pointer flex-shrink-0"
          aria-label="Send message"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>

      <div className="flex items-center justify-between mt-1.5 px-3 text-[10px] text-muted-foreground/60 max-w-5xl mx-auto">
        <span>
          <kbd className="font-mono bg-muted px-1 rounded text-[9px]">Enter</kbd> to send, <kbd className="font-mono bg-muted px-1 rounded text-[9px]">Shift + Enter</kbd> for new line
        </span>
        <span className="flex items-center gap-1">
          <Shield className="w-3 h-3 text-emerald-500" /> End-to-end encrypted workspace
        </span>
      </div>
    </div>
  );
}

interface ConversationInfoPanelProps {
  conversation: ChatConversation;
  onClose: () => void;
  users: UserOption[];
  onAddMembers: (memberIds: string[]) => void;
  addingMembers: boolean;
}

function ConversationInfoPanel({
  conversation,
  onClose,
  users,
  onAddMembers,
  addingMembers,
}: ConversationInfoPanelProps) {
  const [memberSearch, setMemberSearch] = useState('');
  const [showAddPeople, setShowAddPeople] = useState(false);
  const [selectedToAdd, setSelectedToAdd] = useState<string[]>([]);
  const members = conversation.members || [];

  // Only a group has an admin who can invite: department and company chats take
  // everyone from the workspace on their own, and a DM is always two people.
  const canAddPeople = conversation.type === 'group' && conversation.isAdmin;

  useEffect(() => {
    setShowAddPeople(false);
    setSelectedToAdd([]);
  }, [conversation.id]);

  const addableUsers = useMemo(() => {
    const current = new Set(members.map((m) => m.id));
    return users.filter((u) => !current.has(u.id));
  }, [users, members]);

  const filteredMembers = useMemo(() => {
    if (!memberSearch.trim()) return members;
    return members.filter(
      (m) =>
        m.name.toLowerCase().includes(memberSearch.toLowerCase()) ||
        (m.department && m.department.toLowerCase().includes(memberSearch.toLowerCase()))
    );
  }, [members, memberSearch]);

  return (
    <div className="w-80 border-l border-border bg-card flex flex-col h-full z-10 animate-in slide-in-from-right duration-200">
      <div className="p-4 border-b border-border flex items-center justify-between">
        <h3 className="font-bold text-sm text-foreground">Chat Details</h3>
        <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-5 text-center border-b border-border/60 bg-muted/20">
        <div
          className={`w-16 h-16 rounded-2xl flex items-center justify-center font-bold text-xl shadow-xs mx-auto mb-3 ${
            conversation.type === 'company'
              ? 'bg-gradient-to-tr from-sky-500 to-blue-600 text-white'
              : conversation.type === 'department'
              ? 'bg-gradient-to-tr from-emerald-500 to-teal-600 text-white'
              : conversation.type === 'group'
              ? 'bg-gradient-to-tr from-amber-500 to-orange-600 text-white'
              : getAvatarBg(conversation.id)
          }`}
        >
          {conversation.type === 'company' && <Building2 className="w-8 h-8" />}
          {conversation.type === 'department' && <Hash className="w-8 h-8" />}
          {conversation.type === 'group' && <Users className="w-8 h-8" />}
          {conversation.type === 'dm' && getInitials(conversation.name)}
        </div>
        <h4 className="font-bold text-sm text-foreground">{conversation.name || 'Direct Message'}</h4>
        <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider bg-primary/10 text-primary mt-1.5 inline-block">
          {conversation.type}
        </span>
        {conversation.department && (
          <p className="text-xs text-muted-foreground mt-2">Department: {conversation.department}</p>
        )}
      </div>

      <div className="p-4 flex-1 overflow-y-auto space-y-3">
        <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
          <span>PARTICIPANTS ({members.length})</span>
          {canAddPeople && (
            <button
              onClick={() => setShowAddPeople((v) => !v)}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold text-primary hover:bg-primary/10 border border-primary/30 transition-colors cursor-pointer"
            >
              <UserPlus className="w-3.5 h-3.5" />
              Add people
            </button>
          )}
        </div>

        {showAddPeople && (
          <div className="p-3 rounded-xl border border-border/70 bg-muted/20 space-y-2.5">
            <p className="text-[11px] font-semibold text-foreground">Add employees to this group</p>
            {addableUsers.length === 0 ? (
              <p className="text-[11px] text-muted-foreground">Everyone in your workspace is already in this group.</p>
            ) : (
              <>
                <MultiSelect
                  options={addableUsers.map((u) => ({ value: u.id, label: optionLabel(u) }))}
                  value={selectedToAdd}
                  onChange={setSelectedToAdd}
                  placeholder="Select people..."
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setShowAddPeople(false);
                      setSelectedToAdd([]);
                    }}
                    className="px-3 py-1.5 text-[11px] font-semibold text-muted-foreground hover:bg-muted rounded-lg transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      onAddMembers(selectedToAdd);
                      setSelectedToAdd([]);
                      setShowAddPeople(false);
                    }}
                    disabled={selectedToAdd.length === 0 || addingMembers}
                    className="px-3 py-1.5 text-[11px] font-semibold bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-all cursor-pointer flex items-center gap-1.5"
                  >
                    {addingMembers && <Loader2 className="w-3 h-3 animate-spin" />}
                    <span>Add {selectedToAdd.length || ''}</span>
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {(conversation.type === 'department' || conversation.type === 'company') && (
          <p className="text-[11px] text-muted-foreground">
            Everyone in the workspace is added automatically.
          </p>
        )}

        {members.length > 5 && (
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Find member..."
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 bg-muted/40 border border-border/60 rounded-lg text-xs outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        )}

        {filteredMembers.length > 0 ? (
          <div className="space-y-1.5">
            {filteredMembers.map((member) => (
              <div key={member.id} className="flex items-center gap-2.5 p-2 rounded-xl hover:bg-muted/50 transition-colors">
                <div
                  className={`w-7 h-7 rounded-lg flex items-center justify-center font-bold text-[10px] shadow-2xs ${getAvatarBg(
                    member.id
                  )}`}
                >
                  {getInitials(member.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-foreground truncate">{member.name}</p>
                  {member.department && (
                    <p className="text-[10px] text-muted-foreground truncate">{member.department}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : members.length === 0 ? null : (
          <p className="text-xs text-muted-foreground text-center py-4">No matching members found.</p>
        )}
      </div>
    </div>
  );
}

type CreateConversationInput =
  | { type: 'dm'; userId: string }
  | { type: 'group'; name: string; memberIds: string[] };

const optionLabel = (user: UserOption) => (user.department ? `${user.name} — ${user.department}` : user.name);

interface CreateConversationModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'dm' | 'group';
  users: UserOption[];
  onSubmit: (data: CreateConversationInput) => void;
  loading: boolean;
}

function CreateConversationModal({ isOpen, onClose, type, users, onSubmit, loading }: CreateConversationModalProps) {
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [groupName, setGroupName] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [searchFilter, setSearchFilter] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedUser('');
      setGroupName('');
      setSelectedMembers([]);
      setSearchFilter('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const filteredUsers = searchFilter.trim()
    ? users.filter(
        (u) =>
          u.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
          (u.department && u.department.toLowerCase().includes(searchFilter.toLowerCase()))
      )
    : users;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-in fade-in duration-150" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-150" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-muted/20 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold">
              {type === 'dm' ? <Plus className="w-4 h-4" /> : <Users className="w-4 h-4" />}
            </div>
            <div>
              <h2 className="text-sm font-bold text-foreground">
                {type === 'dm' ? 'New Direct Message' : 'Create Team Group'}
              </h2>
              <p className="text-[11px] text-muted-foreground">
                {type === 'dm' ? 'Select a colleague to start 1-on-1 messaging' : 'Group messaging for project or team collaboration'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto min-h-0">
          {type === 'dm' ? (
            <div className="space-y-3">
              <label className="block text-xs font-semibold text-foreground">Select Recipient</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Filter users..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-muted/40 border border-border rounded-xl text-xs outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
              <div className="max-h-60 overflow-y-auto space-y-1 border border-border/60 rounded-xl p-1 bg-muted/10">
                {filteredUsers.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => setSelectedUser(u.id)}
                    className={`w-full p-2.5 rounded-lg flex items-center gap-2.5 text-left text-xs transition-colors cursor-pointer ${
                      selectedUser === u.id
                        ? 'bg-primary text-primary-foreground font-semibold shadow-2xs'
                        : 'hover:bg-muted text-foreground'
                    }`}
                  >
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center font-bold text-[10px] ${getAvatarBg(u.id)}`}>
                      {getInitials(u.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="truncate">{u.name}</p>
                      {u.department && (
                        <p className={`text-[10px] truncate ${selectedUser === u.id ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                          {u.department}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Group Name</label>
                <input
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="e.g. Sales Sprint, UI Redesign, Operations"
                  className="w-full px-3.5 py-2.5 bg-muted/40 border border-border rounded-xl text-xs text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  maxLength={120}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Add Members</label>
                <MultiSelect
                  options={users.map((u) => ({ value: u.id, label: optionLabel(u) }))}
                  value={selectedMembers}
                  onChange={setSelectedMembers}
                  placeholder="Select team members..."
                />
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-border bg-muted/15 flex justify-end gap-2.5 shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-xs font-semibold text-muted-foreground hover:bg-muted rounded-xl transition-colors cursor-pointer">
            Cancel
          </button>
          <button
            onClick={() => {
              if (type === 'dm' && selectedUser) {
                onSubmit({ type: 'dm', userId: selectedUser });
              } else if (type === 'group' && groupName.trim() && selectedMembers.length > 0) {
                onSubmit({ type: 'group', name: groupName.trim(), memberIds: selectedMembers });
              }
            }}
            disabled={loading || (type === 'dm' ? !selectedUser : !groupName.trim() || selectedMembers.length === 0)}
            className="px-5 py-2 bg-primary text-primary-foreground rounded-xl text-xs font-semibold hover:bg-primary/90 disabled:opacity-50 transition-all shadow-xs cursor-pointer flex items-center gap-1.5"
          >
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            <span>{type === 'dm' ? 'Start Chat' : 'Create Group'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export function ChatPage() {
  const { data: conversations = [], isLoading: conversationsLoading } = useChatConversations();
  const { startDm, createGroup, sendMessage, addMembers, deleteMessage, markRead } = useChatMutations();
  const { data: users = [] } = useAllUsers();

  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [showCreateDm, setShowCreateDm] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const activeConversation = conversations.find((c) => c.id === activeConversationId) || null;

  const {
    data: messagesData,
    isLoading: messagesLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useChatMessages(activeConversationId);

  const messages = useMemo(() => messagesFromPages(messagesData), [messagesData]);

  useChatRealtime(conversations.map((c) => c.id));

  const markReadMutate = markRead.mutate;
  useEffect(() => {
    if (activeConversationId) {
      markReadMutate(activeConversationId);
    }
  }, [activeConversationId, messages.length, markReadMutate]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleSendMessage = useCallback(
    (content: string) => {
      if (!activeConversationId) return;
      sendMessage.mutate(
        { conversationId: activeConversationId, content, replyToId: replyingTo?.id },
        {
          onSuccess: () => setReplyingTo(null),
          onError: (err) => toast.error(getApiErrorMessage(err, 'send the message')),
        }
      );
    },
    [activeConversationId, replyingTo, sendMessage]
  );

  const handleDeleteMessage = useCallback(
    (message: ChatMessage) => {
      if (!activeConversationId) return;
      deleteMessage.mutate(
        { conversationId: activeConversationId, messageId: message.id },
        { onError: (err) => toast.error(getApiErrorMessage(err, 'delete the message')) }
      );
    },
    [activeConversationId, deleteMessage]
  );

  const handleAddMembers = useCallback(
    (memberIds: string[]) => {
      if (!activeConversationId || !memberIds.length) return;
      addMembers.mutate(
        { conversationId: activeConversationId, memberIds },
        {
          onSuccess: (result) => toast.success(`${result.added} ${result.added === 1 ? 'person' : 'people'} added`),
          onError: (err) => toast.error(getApiErrorMessage(err, 'add the people')),
        }
      );
    },
    [activeConversationId, addMembers]
  );

  useEffect(() => {
    setReplyingTo(null);
  }, [activeConversationId]);

  const handleCreateDm = useCallback(
    (data: CreateConversationInput) => {
      if (data.type !== 'dm') return;
      startDm.mutate(data.userId, {
        onSuccess: (result) => {
          setShowCreateDm(false);
          setActiveConversationId(result.id);
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'start the conversation')),
      });
    },
    [startDm]
  );

  const handleCreateGroup = useCallback(
    (data: CreateConversationInput) => {
      if (data.type !== 'group') return;
      createGroup.mutate(
        { name: data.name, memberIds: data.memberIds },
        {
          onSuccess: (result) => {
            setShowCreateGroup(false);
            setActiveConversationId(result.id);
          },
          onError: (err) => toast.error(getApiErrorMessage(err, 'create the group')),
        }
      );
    },
    [createGroup]
  );

  const didAutoSelect = useRef(false);
  useEffect(() => {
    if (didAutoSelect.current || conversationsLoading || !conversations.length) return;
    didAutoSelect.current = true;
    setActiveConversationId(conversations[0].id);
  }, [conversations, conversationsLoading]);

  if (conversationsLoading) {
    return (
      <div className="h-[calc(100vh-theme(spacing.16))] flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center animate-bounce">
            <MessageSquare className="w-6 h-6" />
          </div>
          <p className="text-xs font-semibold text-muted-foreground animate-pulse">Loading ZYNX Chats...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-theme(spacing.16))] flex overflow-hidden bg-background">
      {/* Sidebar List */}
      <aside
        className={`w-full lg:w-80 xl:w-96 border-r border-border bg-card/40 flex-shrink-0 flex flex-col transition-all ${
          activeConversationId ? 'hidden lg:flex' : 'flex'
        }`}
      >
        <ConversationList
          conversations={conversations}
          activeId={activeConversationId}
          onSelect={(id) => {
            setActiveConversationId(id);
          }}
          onOpenCreateDm={() => setShowCreateDm(true)}
          onOpenCreateGroup={() => setShowCreateGroup(true)}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </aside>

      {/* Main Chat Area */}
      <main
        className={`flex-1 flex flex-col min-w-0 bg-background overflow-hidden ${
          !activeConversationId ? 'hidden lg:flex' : 'flex'
        }`}
      >
        {activeConversation ? (
          <div className="flex-1 flex overflow-hidden">
            <div className="flex-1 flex flex-col min-w-0">
              <ChatAreaHeader
                conversation={activeConversation}
                onBack={() => setActiveConversationId(null)}
                onToggleInfo={() => setIsInfoOpen(!isInfoOpen)}
                isInfoOpen={isInfoOpen}
              />
              <MessageList
                messages={messages}
                onLoadMore={handleLoadMore}
                hasMore={!!hasNextPage}
                loading={isFetchingNextPage}
                messagesEndRef={messagesEndRef}
                conversationName={activeConversation.name}
                onReply={setReplyingTo}
                onDelete={handleDeleteMessage}
              />
              <MessageInput
                conversation={activeConversation}
                onSend={handleSendMessage}
                disabled={messagesLoading}
                replyTo={replyingTo}
                onCancelReply={() => setReplyingTo(null)}
              />
            </div>

            {/* Collapsible Info Drawer */}
            {isInfoOpen && (
              <ConversationInfoPanel
                conversation={activeConversation}
                onClose={() => setIsInfoOpen(false)}
                users={users}
                onAddMembers={handleAddMembers}
                addingMembers={addMembers.isPending}
              />
            )}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-muted/10 p-6">
            <div className="text-center max-w-sm space-y-4">
              <div className="w-16 h-16 rounded-3xl bg-primary/10 text-primary flex items-center justify-center mx-auto shadow-sm">
                <MessageSquare className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-base font-bold text-foreground">Welcome to ZYNX Chats</h2>
                <p className="text-xs text-muted-foreground mt-1">
                  Connect with team members across direct messages, departmental channels, and project groups.
                </p>
              </div>
              <div className="flex items-center justify-center gap-2 pt-2">
                <button
                  onClick={() => setShowCreateDm(true)}
                  className="px-4 py-2 bg-card hover:bg-muted text-foreground border border-border rounded-xl text-xs font-semibold transition-all shadow-2xs flex items-center gap-1.5 cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5 text-primary" />
                  <span>New Message</span>
                </button>
                <button
                  onClick={() => setShowCreateGroup(true)}
                  className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl text-xs font-semibold transition-all shadow-xs flex items-center gap-1.5 cursor-pointer"
                >
                  <Users className="w-3.5 h-3.5" />
                  <span>New Group</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Modals */}
      <CreateConversationModal
        isOpen={showCreateDm}
        onClose={() => setShowCreateDm(false)}
        type="dm"
        users={users}
        onSubmit={handleCreateDm}
        loading={startDm.isPending}
      />
      <CreateConversationModal
        isOpen={showCreateGroup}
        onClose={() => setShowCreateGroup(false)}
        type="group"
        users={users}
        onSubmit={handleCreateGroup}
        loading={createGroup.isPending}
      />
    </div>
  );
}
